import React, { useState } from 'react';

import {
  VerticalAlignBottomOutlined,
  VerticalAlignCenterOutlined,
  VerticalAlignTopOutlined,
} from '@mui/icons-material';
import { ImageProps, ImagePropsSchema } from '@usewaypoint/block-image';
import { Image } from 'lucide-react';

import BaseSidebarPanel, { SectionHeader } from './helpers/BaseSidebarPanel';
import RadioGroupInput from './helpers/inputs/RadioGroupInput';
import TextDimensionInput from './helpers/inputs/TextDimensionInput';
import TextInput from './helpers/inputs/TextInput';
import MultiStylePropertyPanel from './helpers/style-inputs/MultiStylePropertyPanel';
import { Separator } from '@/components/ui/separator';
import { ToggleGroupItem } from '@/components/ui/toggle-group';

type ImageSidebarPanelProps = {
  data: ImageProps;
  setData: (v: ImageProps) => void;
};

export default function ImageSidebarPanel({ data, setData }: ImageSidebarPanelProps) {
  const [, setErrors] = useState<Zod.ZodError | null>(null);

  const updateData = (d: unknown) => {
    const res = ImagePropsSchema.safeParse(d);
    if (res.success) {
      setData(res.data);
      setErrors(null);
    } else {
      setErrors(res.error);
    }
  };

  return (
    <BaseSidebarPanel title="Image block">
      {/* IMAGE SECTION */}
      <div className="space-y-4">
        <SectionHeader icon={Image}>Image</SectionHeader>
        <div className="space-y-3">
          <TextInput
            label="Source URL"
            defaultValue={data.props?.url ?? ''}
            onChange={(v) => {
              const url = v.trim().length === 0 ? null : v.trim();
              updateData({ ...data, props: { ...data.props, url } });
            }}
          />
          <TextInput
            label="Alt text"
            defaultValue={data.props?.alt ?? ''}
            onChange={(alt) => updateData({ ...data, props: { ...data.props, alt } })}
          />
          <TextInput
            label="Click through URL"
            defaultValue={data.props?.linkHref ?? ''}
            onChange={(v) => {
              const linkHref = v.trim().length === 0 ? null : v.trim();
              updateData({ ...data, props: { ...data.props, linkHref } });
            }}
          />
        </div>
      </div>

      <Separator className="my-6" />

      {/* DIMENSIONS SECTION */}
      <div className="space-y-4">
        <SectionHeader>Dimensions</SectionHeader>
        <div className="flex gap-2">
          <TextDimensionInput
            label="Width"
            defaultValue={data.props?.width}
            onChange={(width) => updateData({ ...data, props: { ...data.props, width } })}
          />
          <TextDimensionInput
            label="Height"
            defaultValue={data.props?.height}
            onChange={(height) => updateData({ ...data, props: { ...data.props, height } })}
          />
        </div>
      </div>

      <Separator className="my-6" />

      {/* LAYOUT SECTION */}
      <div className="space-y-4">
        <SectionHeader>Layout</SectionHeader>
        <div className="space-y-3">
          <RadioGroupInput
            label="Alignment"
            defaultValue={data.props?.contentAlignment ?? 'middle'}
            onChange={(contentAlignment) => updateData({ ...data, props: { ...data.props, contentAlignment } })}
          >
            <ToggleGroupItem value="top">
              <VerticalAlignTopOutlined fontSize="small" />
            </ToggleGroupItem>
            <ToggleGroupItem value="middle">
              <VerticalAlignCenterOutlined fontSize="small" />
            </ToggleGroupItem>
            <ToggleGroupItem value="bottom">
              <VerticalAlignBottomOutlined fontSize="small" />
            </ToggleGroupItem>
          </RadioGroupInput>
          <MultiStylePropertyPanel
            names={['backgroundColor', 'textAlign', 'padding']}
            value={data.style}
            onChange={(style) => updateData({ ...data, style })}
          />
        </div>
      </div>
    </BaseSidebarPanel>
  );
}
